import { IconSearch } from '@tabler/icons-react'
import { CloseButton, Stack, Tabs, Text, TextInput } from 'components'
import { useArcTranslation } from 'hooks'
import { useState } from 'react'
import { SEARCH_ICON_SIZE, TAB_ICON_SIZE } from '../constants'
import { getHubStyle } from '../hubStyles'
import { useArticleFilters } from '../hooks/useArticleFilters'
import type { SanityHubWithArticles } from '../interfaces'
import { ArticleListCard } from './ArticleListCard'
import { ArticleRow } from './ArticleRow'
import { HubHeader } from './HubHeader'
import { TagFilterChips } from './TagFilterChips'

interface ResourceCenterTabsProps {
  hubs: SanityHubWithArticles[]
}

// Search bar + tag filter + hub tabs. While a search or tag filter is active
// the tabs give way to a flat cross-hub result list with hub badges.
export const ResourceCenterTabs = ({ hubs }: ResourceCenterTabsProps) => {
  const { t } = useArcTranslation()
  const filters = useArticleFilters(hubs)
  // Controlled so the active hub survives the tabs unmounting during a
  // search; falls back to the first hub when the stored slug no longer
  // exists (hub slugs are per-language documents).
  const [activeTab, setActiveTab] = useState<string | null>(null)
  const currentTab = activeTab && hubs.some(hub => hub.slug === activeTab) ? activeTab : hubs[0]?.slug

  if (hubs.length === 0) {
    return (
      <Text color='dimmed' size='sm'>
        {t('resource_center.empty')}
      </Text>
    )
  }

  return (
    <Stack spacing='lg'>
      <TextInput
        value={filters.query}
        onChange={event => filters.setQuery(event.currentTarget.value)}
        placeholder={t('resource_center.search_placeholder')}
        icon={<IconSearch size={SEARCH_ICON_SIZE} />}
        rightSection={
          filters.query ? (
            <CloseButton aria-label={t('resource_center.clear')} onClick={() => filters.setQuery('')} />
          ) : null
        }
        size='md'
        radius='xl'
      />

      {filters.allTags.length > 0 ? (
        <TagFilterChips
          visibleTags={filters.visibleTags}
          overflowTags={filters.overflowTags}
          selectedTags={filters.selectedTags}
          onChange={filters.setSelectedTags}
        />
      ) : null}

      {filters.showFlatResults ? (
        <ArticleListCard
          isEmpty={filters.results.length === 0}
          emptyMessage={t(filters.isSearching ? 'resource_center.no_results' : 'resource_center.no_tag_matches')}
        >
          {filters.results.map(({ article, hub }) => {
            const style = getHubStyle(hub.slug, hub.color, hub.icon)
            return (
              <ArticleRow
                key={article._id}
                article={article}
                color={style.color}
                hubTag={{ title: hub.title, icon: style.icon }}
              />
            )
          })}
        </ArticleListCard>
      ) : (
        <Tabs value={currentTab} onTabChange={setActiveTab} keepMounted={false}>
          <Tabs.List>
            {hubs.map(hub => {
              const { icon: Icon, color } = getHubStyle(hub.slug, hub.color, hub.icon)
              return (
                <Tabs.Tab key={hub._id} value={hub.slug} color={color} icon={<Icon size={TAB_ICON_SIZE} />}>
                  {hub.title}
                </Tabs.Tab>
              )
            })}
          </Tabs.List>

          {hubs.map(hub => {
            const { color } = getHubStyle(hub.slug, hub.color, hub.icon)
            return (
              <Tabs.Panel key={hub._id} value={hub.slug} pt='xl'>
                <Stack spacing='lg'>
                  <HubHeader hub={hub} />
                  <ArticleListCard isEmpty={hub.articles.length === 0} emptyMessage={t('resource_center.hub_empty')}>
                    {hub.articles.map(article => (
                      <ArticleRow key={article._id} article={article} color={color} />
                    ))}
                  </ArticleListCard>
                </Stack>
              </Tabs.Panel>
            )
          })}
        </Tabs>
      )}
    </Stack>
  )
}
